import path from 'path'
import MarkdownIt from 'markdown-it'
import MarkdownItAttrs from 'markdown-it-attrs'

import { slugify } from '@baserow/modules/core/utils/string'

// eslint-disable-next-line
const external = new RegExp('^(?:[a-z]+:)?//', 'i')

export class MarkdownPage {
  constructor(base, markdown, config) {
    this.base = base
    this.config = config
    this.markdown = markdown
    this.markdownIt = MarkdownIt({
      html: true,
      typographer: true,
      linkify: true,
    })
    this.markdownIt.use(MarkdownItAttrs)
    this.parsed = this.markdownIt.parse(this.markdown, { references: {} })
    this.updateLinkTokens(this.parsed)

    this.toc = this.extractTableOfContents(this.parsed)
    this.title = this.extractTitle(this.parsed)
    this.beforeRender()
    this.html = this.markdownIt.renderer.render(
      this.parsed,
      this.markdownIt.options,
      {}
    )
  }

  /**
   * An optional hook called before the provided page is rendered for sub classes to
   * override to do further render customization.
   */
  beforeRender() {}

  /**
   * Returns the content of the first h1 as plaintext.
   */
  extractTitle(tokens) {
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (token.type === 'heading_open' && token.tag === 'h1') {
        const stripped = this.plainTextTokenContent(tokens[i + 1])
        return stripped
      }
    }
  }

  /**
   * Extracts an object containing the table of contents. The first level are all
   * the h2 elements and the second level are all the h3 elements.
   */
  extractTableOfContents(tokens) {
    const toc = []
    let item = null

    // Adds the menu item to the menu list
    const flush = () => {
      if (item !== null) {
        toc.push(item)
      }
    }

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (token.type === 'heading_open' && token.tag === 'h2') {
        const name = this.plainTextTokenContent(tokens[i + 1])
        const id = slugify(name)
        this.setTokenAttribute(token, 'id', id)
        flush()
        item = { id, name, children: [] }
      }

      if (
        token.type === 'heading_open' &&
        token.tag === 'h3' &&
        item !== null
      ) {
        const name = this.plainTextTokenContent(tokens[i + 1])
        const id = slugify(name)
        this.setTokenAttribute(token, 'id', id)
        item.children.push({ id, name })
      }
    }

    flush()
    return toc
  }

  /**
   * Strips all the html tags from the provided string.
   */
  stripTags(html) {
    return html.replace(/<[^>]*>?/gm, '')
  }

  /**
   * Renders all the children of a token to a string and then strips the text to
   * convert it to plaintext.
   */
  plainTextTokenContent(token) {
    const rendered = this.markdownIt.renderer.render(token.children)
    const stripped = this.stripTags(rendered)
    const replaced = stripped.replace(/&amp;/g, '&')
    return replaced
  }

  /**
   * Recursively searches for links in the tokens. If the link is external the
   * _blank and nofollow attributes are added. If the link is an internal link to
   * another markdown file it will be converted to an absolute link.
   */
  updateLinkTokens(tokens) {
    tokens.forEach((token) => {
      // Check if the provided token is a link and if
      if (token.type === 'link_open') {
        const href = this.getTokenAttribute(token, 'href')
        if (this.isPublicWebFrontendLink(href)) {
          // If the link starts with `https://baserow.io`, for example, then we want to
          // make it relative and dofollow.
          this.setTokenAttribute(
            token,
            'href',
            this.prepareWebFrontendLink(href)
          )
        } else if (this.isExternalLink(href)) {
          // If the link is external we want to add the following target and rel
          // attributes.
          this.setTokenAttribute(token, 'target', '_blank')
          this.setTokenAttribute(token, 'rel', 'nofollow')
        } else if (this.isInternalLink(href)) {
          // If the link is internal we want to convert it to an absolute path.
          this.setTokenAttribute(
            token,
            'href',
            this.prepareInternalLink(this.base, href)
          )
        }
      }

      // Recursively check if there are children that need to be updated.
      if (token.children !== null) {
        this.updateLinkTokens(token.children)
      }
    })
  }

  /**
   * Set or replace the attribute with the provided name.
   */
  setTokenAttribute(token, name, value) {
    let changed = false
    if (token.attrs === null) {
      token.attrs = []
    }
    token.attrs.forEach((attr) => {
      if (attr[0] === name) {
        changed = true
        attr[1] = value
      }
    })
    if (!changed) {
      token.attrs.push([name, value])
    }
  }

  /**
   * Returns the value of the tokens attribute.
   */
  getTokenAttribute(token, name) {
    let value
    token.attrs.forEach((attr) => {
      if (attr[0] === name) {
        value = attr[1]
      }
    })
    return value
  }

  /**
   * Indicates if the href value is an internal link to another markdown file.
   */
  isInternalLink(href) {
    return (
      href.slice(href.length - 3) === '.md' &&
      href.slice(0, 1) !== '/' &&
      !external.test(href)
    )
  }

  /**
   * Indicates if the href value is an external link.
   */
  isExternalLink(href) {
    return external.test(href)
  }

  /**
   * Converts the provided relative path to an absolute path.
   */
  prepareInternalLink(base, relativePath) {
    let href = base.substring(0, base.lastIndexOf('/'))
    href = path.join(href, relativePath)

    if (href.endsWith('.md')) {
      href = href.slice(0, href.length - 3)
    }

    return href
  }

  isPublicWebFrontendLink(href) {
    const publicWebFrontend = this.config.PUBLIC_WEB_FRONTEND_URL
    return href.startsWith(publicWebFrontend)
  }

  prepareWebFrontendLink(href) {
    const publicWebFrontend = this.config.PUBLIC_WEB_FRONTEND_URL
    return href.slice(publicWebFrontend.length)
  }
}
